// Сессия: токен, тариф, остатки. Одно место, где живёт «кто вошёл».
//
// Токен в localStorage, а не в httpOnly-куке. Довод был такой: EventSource
// заголовки задавать не умеет, поток этапов получал токен параметром запроса, и
// кука не закрыла бы его от XSS, читающего адрес потока. С 17.09.2026 в адресе
// одноразовый билет (SMY-221), а с 24.09.2026 токен там не принимается вовсе —
// этот довод отпал. Остаётся CSRF-защита, которую кука потребовала бы на каждый
// POST; владелец 24.09.2026 решил оставить localStorage и вернуться к вопросу
// после первых живых пользователей (разбор — docs/data-flow.md, Э6.2). Для P9
// хранилище всё равно другое: там SecureStore, и потому оно вынесено в
// TokenStore.

import { createContext, use, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  ApiError, createAccount, createArtifacts, createAuth, createBilling, createCatalog, createChats,
  createClient, createComplaints, createConfirmations, createEvents, createFiles, createIntent,
  createProjects, createRuns,
  type AccountApi, type Artifacts, type Auth, type Balances, type BillingApi, type Catalog,
  type ChatsApi, type ComplaintsApi, type Confirmations, type EventsApi, type FilesApi,
  type IntentApi, type Me,
  type Projects,
  type Reference, type Runs, REQUEST_REJECTED,
} from '@smyslika/api-client'
import { WEB_DEVICE } from '@smyslika/core'

import { createBrowserTokens } from './token-store.ts'

/**
 * Где лежит токен — `token-store.ts`: гость во вкладке, признанный аккаунт
 * навсегда. Правило там и проверяется (`token-store.test.ts`).
 */
const tokens = createBrowserTokens({ local: () => localStorage, session: () => sessionStorage })
const browserTokens = tokens.store

/**
 * Ответ `/v1/me` — хранилищу токена: не гость переезжает в постоянное
 * (`settle` в token-store.ts, там же правило и проверка).
 *
 * Здесь, а не у каждой формы с кодом: гость становится аккаунтом и на экране
 * входа, и в окне перед оплатой (SMY-341), и форма, забывшая переложить токен,
 * оставила бы только что заведённый аккаунт жить до закрытия вкладки.
 */
const settle = (me: Me): Me => {
  tokens.settle(me)
  return me
}

/**
 * Токен текущей сессии — для запросов мимо клиента API. SMY-243
 *
 * Такой запрос один: выгрузка данных, которую отдаёт поток, а не JSON. До
 * 21.09.2026 он читал `localStorage` сам и гостя не видел — гость живёт в
 * `sessionStorage`, и выгрузка отвечала ему 401, хотя п. 5.4 политики обещает
 * её гостю так же, как зарегистрированному. Читать токен вторым путём мимо
 * хранилища — значит однажды снова разойтись с ним.
 *
 * Им же экран чата сверяет сеанс попытки с неизвестным исходом (SMY-449):
 * попытка помнит токен, которым ушла, и под другим не встаёт и не досылается.
 * Тем же путём, а не своим (ворота ревью 6, dedup-hunter).
 */
export function sessionToken(): string | null {
  return browserTokens.get()
}

export interface SessionValue {
  auth: Auth
  runs: Runs
  catalog: Catalog
  projects: Projects
  chats: ChatsApi
  intent: IntentApi
  confirmations: Confirmations
  /**
   * Что человек выбрал на экране 04. SMY-82
   *
   * Единственное, о чём клиенту приходится рассказывать серверу самому:
   * отмену — уход с экрана — сервер не видит ничем.
   */
  events: EventsApi
  artifacts: Artifacts
  files: FilesApi
  account: AccountApi
  billing: BillingApi
  /** Жалобы и оспаривание блокировки. Экран 11 (SMY-80). */
  complaints: ComplaintsApi
  /** Время сервера: от него считается окно бесплатной правки. */
  serverNow: () => Date
  /** null — ещё не проверяли, false — не вошли, true — вошли. */
  signedIn: boolean | null
  /**
   * Гостевая ли сессия. SMY-175
   *
   * Считается по `me.signup`: состояние лестницы сервер отдаёт ТОЛЬКО гостю
   * (`stateOf` возвращает null всем, у кого есть подтверждённая почта или
   * номер). Отдельного поля заводить не стали намеренно — второе поле про то
   * же самое однажды разойдётся с первым, и разойдётся молча.
   */
  guest: boolean
  /**
   * Сессия кончилась сама, а не человек вышел.
   *
   * Различать обязательно: выкинутый на экран входа без объяснения решает,
   * что его разлогинили нарочно или что что-то сломалось. Своим «выйти» он
   * этого не подумает.
   */
  expired: boolean
  /**
   * Сколько раз за жизнь страницы гостю молча заведён новый гость. SMY-357
   *
   * Растёт, когда сервер отверг токен гостя — срок сессии, уборка брошенных
   * гостей, подменённый токен. Войти обратно гостю нечем, почты у него нет, и
   * вместо экрана входа «войди той же почтой» он остаётся на месте новым
   * гостем. Экран чата по этому числу объясняет, что случилось, и уходит из
   * чата прежнего гостя.
   */
  guestRenewals: number
  /** Кто вошёл: почта, имя и тариф. Карточка аккаунта на экране 11. */
  me: Me | null
  tariff: string | null
  reference: Reference | null
  balances: Balances | null
  refreshBalances: () => Promise<void>
  /**
   * Перечитать карточку человека, не трогая состояние входа.
   *
   * Нужно потому, что в `me` лежат не только имя и тариф, но и два признака,
   * которые МЕНЯЮТСЯ во время работы: `hasResult` (появился первый готовый
   * ответ) и `hasPassword` (пароль только что завели). От них зависят
   * модалки, и без обновления предложение завести пароль всплыло бы только
   * после перезагрузки страницы — то есть в случайный момент, а не сразу
   * после первого результата.
   */
  refreshMe: () => Promise<void>
  signOut: () => Promise<void>
  /** Проставляется после успешного ввода кода на экране 01. */
  signedInNow: () => Promise<void>
}

const SessionContext = createContext<SessionValue | null>(null)

export function useSession(): SessionValue {
  const value = use(SessionContext)
  if (value == null) throw new Error('useSession вне SessionProvider')
  return value
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [signedIn, setSignedIn] = useState<boolean | null>(null)
  const [expired, setExpired] = useState(false)
  const [me, setMe] = useState<Me | null>(null)
  const [reference, setReference] = useState<Reference | null>(null)
  const [balances, setBalances] = useState<Balances | null>(null)
  const [guestRenewals, setGuestRenewals] = useState(0)
  /** Продление гостя — функцией из эффекта ниже: клиент заводится раньше `load`. */
  const renewGuest = useRef<() => void>(() => {})

  // baseUrl пустой: статику и API отдаёт один origin, и это условие того,
  // что CORS не нужен. Меняя это, придётся заводить CORS на сервере.
  const api = useMemo(() => {
    const client = createClient({
      baseUrl: '',
      tokens: browserTokens,
      // Отказ не нашего сервера чаще всего значит вкладку, открытую до
      // выкладки: шаг — обновить страницу (SMY-353).
      rejected: REQUEST_REJECTED.web,
      // Сервер различает iOS: без платформы он считает клиента iOS и
      // выключает типы решения 18 PRD каталога и where_to_buy (SMY-494, SMY-557).
      platform: 'web',
      onUnauthorized: () => {
        // Сюда попадают только запросы, ушедшие С токеном: без токена
        // `load` даже не спрашивает сервер. Значит, токен был и его не
        // приняли — сессия истекла или её отозвали с другого устройства.
        //
        // ⚠️ ГОСТЮ — НЕ ЭКРАН ВХОДА (SMY-357, 05.10.2026). До этого гость с
        // кончившейся сессией попадал на `/login` и читал «войди той же
        // почтой», а почты у него нет; вопрос из поля пропадал, пути назад не
        // было. Теперь ему молча заводится новый гость, и он остаётся на
        // месте с текстом в поле. Гостем ли был токен, помнит хранилище — к
        // этому моменту клиент его уже стёр.
        if (tokens.lostGuest()) {
          renewGuest.current()
          return
        }
        setExpired(true)
        setSignedIn(false)
        setMe(null)
        setBalances(null)
      },
    })
    return {
      client,
      auth: createAuth(client),
      runs: createRuns(client),
      catalog: createCatalog(client),
      projects: createProjects(client),
      chats: createChats(client),
      intent: createIntent(client),
      confirmations: createConfirmations(client),
      events: createEvents(client),
      artifacts: createArtifacts(client),
      files: createFiles(client),
      account: createAccount(client),
      billing: createBilling(client),
      complaints: createComplaints(client),
    }
  }, [])

  const refreshBalances = useCallback(async () => {
    try {
      setBalances(await api.catalog.balances())
    } catch {
      // Остатки — украшение шапки. Их отсутствие не повод ломать экран.
    }
  }, [api])

  const refreshMe = useCallback(async () => {
    try {
      setMe(settle(await api.auth.me()))
    } catch {
      // Сюда попадает офлайн; 401 уже разобран в onUnauthorized. Ни то, ни
      // другое не повод менять состояние входа: карточка просто останется
      // прежней, а модалка не всплывёт — это лучше, чем выкинуть человека.
    }
  }, [api])

  /** Идущее продление гостя (`renewGuest` ниже) — `load` его не перебивает. */
  const renewing = useRef<Promise<void> | null>(null)

  const load = useCallback(async () => {
    if (browserTokens.get() == null) {
      // ⚠️ КАБИНЕТ ОТКРЫВАЕТСЯ РАБОТОЙ, А НЕ ФОРМОЙ ВХОДА. SMY-175
      //
      // Гость заводится молча при первом заходе — как в приложении, где
      // аккаунт заводит первый запуск. Раньше здесь стоял отказ, и довод был
      // записан прямо: «устройство» в вебе — это вкладка, а значит приватное
      // окно раздавало бы половину тарифа бесконечно. Довод снят не отменой,
      // а числом: гостю в вебе достаётся ОДНА задача (`webSignupSteps`).
      try {
        const session = await api.auth.startGuest(WEB_DEVICE)
        tokens.keepAsGuest(session.token)
      } catch {
        // Не завёлся — показываем вход, а не пустой экран. Сюда приходит и
        // 429 от лимита гостей с одной сети: человек ничего не сделал, и
        // единственное, что ему остаётся, — войти в существующий аккаунт.
        setSignedIn(false)
        return
      }
    }
    try {
      setMe(settle(await api.auth.me()))
      setSignedIn(true)
      setExpired(false)
      void refreshBalances()
    } catch (e) {
      // 401 уже почистил токен через onUnauthorized; сюда попадает и офлайн,
      // и тогда честнее считать «не вошли», чем показывать пустой кабинет.
      //
      // Кроме гостя, которому onUnauthorized уже заводит нового (SMY-357): на
      // открытии и перезагрузке вкладки «не вошли» отсюда уводило на вход
      // раньше, чем продление кончалось, и новый гость оставался на /login без
      // объяснения. Чем кончилось, решает само продление.
      if (e instanceof ApiError && e.status === 401 && renewing.current != null) return
      setSignedIn(false)
    }
  }, [api, refreshBalances])

  /**
   * Новый гость вместо отвергнутого — тем же `load`, что заводит гостя на
   * первом заходе: токена нет, значит, гость заводится. Одно продление на
   * вкладку: 401 на прежний токен гасит сессию один раз (`client.ts`), а
   * пока продление идёт, второй вызов ждёт его же, а не заводит второго
   * гостя — предел гостей с адреса общий (20 в час). Не вышло (тот же
   * предел, нет связи) — `load` ставит «не вошли», и это экран входа без
   * строки «войди той же почтой»: `expired` здесь не ставится.
   */
  useEffect(() => {
    renewGuest.current = () => {
      renewing.current ??= (async () => {
        await load()
        // Новый гость заведён — экран скажет, что прежняя сессия кончилась. Не
        // заведён — экран входа, как раньше: `load` здесь «не вошли» не ставит.
        if (browserTokens.get() != null) setGuestRenewals((n) => n + 1)
        else setSignedIn(false)
      })().finally(() => {
        renewing.current = null
      })
    }
  }, [load])

  useEffect(() => {
    void load()
    api.catalog.reference().then(setReference).catch(() => setReference(null))
  }, [api, load])

  const value = useMemo<SessionValue>(
    () => ({
      auth: api.auth,
      runs: api.runs,
      catalog: api.catalog,
      projects: api.projects,
      chats: api.chats,
      intent: api.intent,
      confirmations: api.confirmations,
      events: api.events,
      artifacts: api.artifacts,
      files: api.files,
      account: api.account,
      billing: api.billing,
      complaints: api.complaints,
      serverNow: api.client.serverNow,
      signedIn,
      guest: me?.signup != null,
      expired,
      guestRenewals,
      me,
      tariff: me?.tariff ?? null,
      reference,
      balances,
      refreshBalances,
      refreshMe,
      signedInNow: load,
      async signOut() {
        await api.auth.logout()
        // Вышел сам — объяснять нечего.
        setExpired(false)
        setSignedIn(false)
        setMe(null)
        setBalances(null)
      },
    }),
    [api, signedIn, expired, guestRenewals, me, reference, balances, refreshBalances, refreshMe, load],
  )

  return <SessionContext value={value}>{children}</SessionContext>
}
